import { useMemo } from 'react';
import { bindActionCreators } from '@reduxjs/toolkit';
import { useDispatch } from 'react-redux';

/**
 * useActions hook fabric method
 * @param {object} actions - actions object
 *
 * @example
 * // actions.js
 * export const doSomething = e => ({
 *   type: 'sometype',
 *   payload: e.target.value,
 * })
 *
 * export const useActions = createUseActionsHook({ doSomething })
 *
 * // component.js
 * import React from 'react'
 * import { useActions } from './actions'
 *
 * export const MyComponent = ({ id, text }) => {
 *   const {doSomething} = useActions()
 *   return (
 *     <button value={id} onClick={doSomething}>
 *       {text}
 *     </button>
 *   )
 * }
 *
 * @returns {function} `useActions` hook
 */
export function createActionsHook<T>(actions: T) {
  return function useActions(deps: any[]): T {
    const dispatch = useDispatch();
    return useMemo(
      () => {
        if (Array.isArray(actions)) {
          return actions.map((action) => bindActionCreators(action, dispatch));
        }
        return bindActionCreators(actions as any, dispatch);
      },
      deps ? [dispatch, ...deps] : [dispatch],
    ) as T;
  };
}

/**
 * Hook connects dispatch with action
 * @param {object} actions - actions object
 * @param {Array} deps - deps array(optional)
 * @returns {object} actions object covered with dispatch
 */
export function useActions<T>(actions: T, deps?: any[]): T {
  // @ts-ignore
  return createActionsHook(actions)(deps);
}
